/**
 * Extension Accordion Component
 * Displays optional pre-tour or post-tour extensions (multi-day tours)
 * Accordion is closed by default
 */

import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack
} from "@mui/material";
import { useTranslations } from "next-intl";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExtensionSummary
  from "@/components/product-detail-page/itinerary-section/components/pre-post-tour/extension-summary";
import ExtensionDayCard from "./extension-day-card";
import type { ExtensionAccordionProps } from "../../types";

const ExtensionAccordion: React.FC<ExtensionAccordionProps> = ({
  extension,
  type,
  testIdPrefix,
  handleOpenExtensionStyles,
  handleOpenActivityLevel,
  extensionStyleProps
}) => {
  // Format price - handles both number and string prices from API
  const formatPrice = (price: number | string | undefined) => {
    if (typeof price === "number") return price.toLocaleString("en-US");
    return price; // Already formatted string from API
  };

  const t = useTranslations("PDP");
  const title = `${type === "pre" ? `${t("Optional Pre-Tour")}` : `${t("Optional Post-Tour")}`} ${extension.title}`;
  

  return extension ? (
    <Accordion
      sx={{
        backgroundColor: "background.cool_strong",
        borderRadius: "2rem!important",
        boxShadow: 0,
        "&:before": { display: "none" },
        "&:last-child": {
          marginBottom: 0
        }
      }}
      slotProps={{
        heading: {
          component: "div"
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${type}-tour-content`}
        id={`${type}-tour-header`}
        data-testid={`${testIdPrefix}_extension-accordion_${type}`}
        sx={{
          padding: "2.4rem",
          minHeight: "74px",
          "&.Mui-expanded": {
            minHeight: "74px"
          },
          "& .MuiAccordionSummary-content": {
            margin: "0!important"
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            width: "100%",
            paddingRight: "1.2rem"
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontSize: { xs: "1.8rem", md: "2rem" },
              fontWeight: 600,
              lineHeight: 1.3,
              color: "primary.main",
              margin: 0
            }}
            data-testid={`${testIdPrefix}_accordion-title`}
          >
            {title}
          </Typography>

          {extension.price && (
            <Typography
              sx={{
                fontSize: "1.8rem",
                fontWeight: 700,
                lineHeight: 1.5,
                color: "primary.main"
              }}
              data-testid={`${testIdPrefix}_accordion-price`}
            >
              {extension.price && typeof extension.price === "number"
                ? `${t("From")} $${formatPrice(extension.price)} ${t("pp*")}`
                : `${t("From")} ${extension.price} ${t("pp*")}`}
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          padding: "0"
        }}
      >
        <Stack gap="0">
          <ExtensionSummary
            handleOpenExtensionStyles={handleOpenExtensionStyles}
            handleOpenActivityLevel={handleOpenActivityLevel}
            activityLevelDescription={extension.activityLevelDescription}
            activityLevelNumber={extension.activityLevelNumber}
            extensionStyleProps={extensionStyleProps}
            testIdPrefix={testIdPrefix}
            extensionStyle={extension.extensionStyle ?? ""}
            meals={extension.meals ?? ""}
            description={extension.description ?? ""}
          />
          {/* Render each extension day as a card */}
          {extension.days && extension.days.map((day) => (
            <ExtensionDayCard
              key={`${type}-extension-day-${day.dayNumber}`}
              day={day}
              dayNumber={day.dayNumber}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  ) : null;
};

export default ExtensionAccordion;
